/**
 * NOURION Rechenkern, Shop Variante (ES Modul für Browser und Node).
 *
 * Grundlage: Preisregeln des Herstellers (Abrechnungsregel je Position, Mindestabnahme, Umschalter für
 * Backup und Cloud to Cloud Backup). Der Rechenkern rechnet ausschließlich mit Verkaufspreisen (VK):
 * Positionen tragen nur VK, Zeilen, Produktsummen und Gesamtsumme enthalten nur VK.
 *
 * parseMenge liefert bei Fehlern zusätzlich einen Code (komma, dezimal, minus, zeichen). Der Text in
 * error ist technisch; die Oberfläche zeigt eigene Meldungen je Code.
 *
 * Im Shop liefert der Konfigurator die Positionen aus Regeldatei und Katalog (VK aus Shopify,
 * customerPrice = Variantenpreis in Euro) und ruft kalkuliere immer mit c2cJeGb false,
 * mindestVkAusEigenemPreis true und exklusivem C2C Modell auf (Masterplan E8).
 *
 * Keine Importe, keine Globalen, kein Zugriff auf die Seite. Alle Funktionen sind rein.
 */

/**
 * Position der Preisliste. Preisfelder sind ausschließlich VK.
 * @typedef {Object} Position
 * @property {string} product  Produkt ID, zum Beispiel managedRmm
 * @property {string} id  Positions ID innerhalb des Produkts
 * @property {string} label  Anzeigename
 * @property {number} customerPrice  VK netto je Einheit in Euro
 * @property {string} rule  Abrechnungsregel (per_unit, per_unit_min, flag, flag_follows,
 *   per_started_1000, per_started_1000_after_100, per_started_10_or_per_gb)
 * @property {string} [follows]  Bezugsposition bei flag_follows (Schlüssel produkt.id)
 * @property {string} [maxOf]  Obergrenze durch diese Position (Schlüssel produkt.id)
 * @property {string} [billingModel]  workload, gigabyte oder any (nur Backup)
 * @property {string} [storage]  synaxon oder acronis (nur Backup)
 * @property {string} [c2cModel]  perUser oder perStorage (nur Cloud to Cloud Backup)
 * @property {{ quantity: number, customerPrice: number }} [minimumPurchase]  Pflicht bei per_unit_min
 */

/**
 * Rechenkontext einer Position.
 * @typedef {Object} Ctx
 * @property {Record<string, number>} [mengen]  Eingaben je Schlüssel produkt.id (für flag_follows)
 * @property {boolean} [c2cJeGb]  true rechnet C2C Speicher je GB, false je angefangene 10 GB
 * @property {boolean} [mindestVkAusEigenemPreis]  true: Pauschale der Mindestabnahme = Mindestmenge mal VK
 */

/**
 * Eingabe der Gesamtkalkulation.
 * @typedef {Object} Eingabe
 * @property {string[]} [produkte]  gewählte Produkt IDs
 * @property {Record<string, number>} [mengen]  Eingaben je Schlüssel produkt.id
 * @property {Record<string, number>} [preise]  optionale eigene VK je Schlüssel produkt.id
 * @property {{ storage?: 'synaxon'|'acronis', billingModel?: 'workload'|'gigabyte' }} [backup]
 * @property {{ model?: 'perUser'|'perStorage'|'both' }} [c2c]  both rechnet beide Modelle zusammen
 * @property {boolean} [c2cJeGb]
 * @property {boolean} [mindestVkAusEigenemPreis]
 */

/**
 * @typedef {Object} Summe
 * @property {number} vk  VK netto in Euro, auf Cent gerundet
 */

/**
 * @typedef {Object} Zeile
 * @property {string} key  Schlüssel produkt.id
 * @property {string} label
 * @property {number} menge  Eingabe
 * @property {number} abrechnungsmenge
 * @property {number} vk
 */

/** Rundet kaufmännisch auf Cent. */
export function cent(x) {
  return Math.round((x + Number.EPSILON) * 100) / 100;
}

/** Ganze Zahl ohne oder mit Tausenderpunkten ("1.000", "1.000.000"). */
const MUSTER_GANZZAHL = /^\d{1,3}(\.\d{3})*$|^\d+$/;
/** Dezimalzahl mit Punkt und ein oder zwei Nachkommastellen ("1.5", "12.34"). */
const MUSTER_DEZIMAL = /^\d+\.\d{1,2}$/;
/** Führendes Minus, auch typografische Varianten (U+2012, U+2013, U+2014, U+2212). */
const MUSTER_MINUS = /^[-‒–—−]/;

/**
 * Liest eine Menge streng ein. "2,5" wird nicht zu 25, sondern als Fehler gemeldet.
 * Erlaubt: ganze Zahlen ab 0, Tausenderpunkte ("1.000"). Leere Eingabe ergibt 0.
 * Obergrenzen prüft der Aufrufer.
 *
 * Fehlercodes: minus (führendes Minus), komma (enthält ein Komma), dezimal (Punkt mit ein oder
 * zwei Nachkommastellen, zum Beispiel "1.5"), zeichen (alles andere, zum Beispiel "1e3", "1 000").
 *
 * @param {unknown} input
 * @returns {{ ok: true, value: number } |
 *   { ok: false, error: string, code: 'komma'|'dezimal'|'minus'|'zeichen' }}
 */
export function parseMenge(input) {
  let s;
  try {
    s = String(input ?? '').trim();
  } catch {
    return { ok: false, error: 'Bitte nur Ziffern eingeben.', code: 'zeichen' };
  }
  if (s === '') return { ok: true, value: 0 };
  if (MUSTER_MINUS.test(s)) return { ok: false, error: 'Negative Mengen sind nicht erlaubt.', code: 'minus' };
  if (s.includes(',')) return { ok: false, error: 'Bitte ganze Zahl ohne Komma eingeben.', code: 'komma' };
  if (MUSTER_DEZIMAL.test(s)) return { ok: false, error: 'Bitte ganze Zahl eingeben.', code: 'dezimal' };
  if (!MUSTER_GANZZAHL.test(s)) return { ok: false, error: 'Bitte nur Ziffern eingeben.', code: 'zeichen' };
  return { ok: true, value: Number(s.replace(/\./g, '')) };
}

/**
 * Abrechnungsmenge je Regel.
 * @param {Position} pos
 * @param {number} menge  eingegebene Menge (bei Checkbox Positionen 0 oder 1)
 * @param {Ctx} [ctx]
 * @returns {number}
 */
export function abrechnungsmenge(pos, menge, ctx = {}) {
  const n = Math.max(0, Number(menge) || 0);
  switch (pos.rule) {
    case 'per_unit':
    case 'per_unit_min':
      return n;
    case 'flag':
      return n > 0 ? 1 : 0;
    case 'flag_follows':
      return n > 0 ? Math.max(0, Number(ctx.mengen?.[pos.follows]) || 0) : 0;
    case 'per_started_1000':            // angefangene TB, 1 TB = 1.000 GB
      return n <= 0 ? 0 : Math.ceil(n / 1000);
    case 'per_started_1000_after_100':  // API Anfragen: erste 100 frei, dann je angefangene 1.000
      return n <= 100 ? 0 : Math.ceil((n - 100) / 1000);
    case 'per_started_10_or_per_gb':    // C2C Pro Speicher: je angefangene 10 GB, mit ctx.c2cJeGb je GB
      return ctx.c2cJeGb ? n : (n <= 0 ? 0 : Math.ceil(n / 10));
    default:
      throw new Error(`Unbekannte Regel: ${pos.rule}`);
  }
}

/**
 * Summe einer Position. Bei Mindestabnahme (per_unit_min) gilt unter der Mindestmenge die Pauschale:
 * fest minimumPurchase.customerPrice oder, mit mindestVkAusEigenemPreis true, Mindestmenge mal VK
 * (auf Cent gerundet).
 * @param {Position} pos
 * @param {number} menge
 * @param {Ctx} [ctx]
 * @param {number} [vkOverride]  eigener VK je Einheit statt pos.customerPrice
 * @returns {{ abrechnungsmenge: number, vk: number }}
 */
export function positionsSumme(pos, menge, ctx = {}, vkOverride) {
  const c = ctx ?? {};
  const vk = vkOverride ?? pos.customerPrice;
  const n = Math.max(0, Number(menge) || 0);
  const m = abrechnungsmenge(pos, n, c);
  if (pos.rule === 'per_unit_min' && n > 0 && n < pos.minimumPurchase.quantity) {
    const minVk = c.mindestVkAusEigenemPreis ? cent(pos.minimumPurchase.quantity * vk) : pos.minimumPurchase.customerPrice;
    return { abrechnungsmenge: m, vk: minVk };
  }
  return { abrechnungsmenge: m, vk: cent(m * vk) };
}

/**
 * Validierung der Eingabe. Die Warnungen sind technisch (Konsole), nie Oberflächentext.
 * Meldet unbekannte Positionen und Advanced über Basis (maxOf).
 * @param {Position[]} positions
 * @param {Eingabe} eingabe
 * @returns {string[]} Warnungen
 */
export function pruefe(positions, eingabe) {
  const w = [];
  const mengen = eingabe?.mengen ?? {};
  const byKey = new Map(positions.map(p => [`${p.product}.${p.id}`, p]));
  for (const [key, menge] of Object.entries(mengen)) {
    const p = byKey.get(key);
    if (!p) { w.push(`Unbekannte Position: ${key}`); continue; }
    if (p.maxOf && menge > (mengen[p.maxOf] ?? 0)) {
      w.push(`${p.label}: mehr Advanced Lizenzen (${menge}) als Basis (${mengen[p.maxOf] ?? 0}).`);
    }
  }
  return w;
}

/**
 * Gesamtkalkulation.
 * Shop Aufruf (immer so): kalkuliere(positionen, { produkte, mengen, backup, c2c: { model },
 *   c2cJeGb: false, mindestVkAusEigenemPreis: true })
 * @param {Position[]} positions
 * @param {Eingabe} eingabe
 * @returns {{ zeilen: Zeile[], proProdukt: Record<string, Summe>, gesamt: Summe, warnungen: string[] }}
 */
export function kalkuliere(positions, eingabe = {}) {
  const ctx = {
    mengen: eingabe.mengen ?? {},
    c2cJeGb: eingabe.c2cJeGb ?? false,
    mindestVkAusEigenemPreis: eingabe.mindestVkAusEigenemPreis ?? false,
  };
  const backup = { storage: 'synaxon', billingModel: 'workload', ...(eingabe.backup ?? {}) };
  const c2cModel = eingabe.c2c?.model ?? 'perUser';
  const proProdukt = {};
  const zeilen = [];

  for (const p of positions) {
    if (!(eingabe.produkte ?? []).includes(p.product)) continue;
    const key = `${p.product}.${p.id}`;
    const menge = ctx.mengen[key] ?? 0;
    if (!menge) continue;

    // Umschalter Backup
    if (p.product === 'managedBackup') {
      if (p.billingModel === 'workload' && backup.billingModel !== 'workload') continue;
      if (p.billingModel === 'gigabyte' && backup.billingModel !== 'gigabyte') continue;
      if (p.storage && p.storage !== backup.storage) continue;
    }
    // Umschalter C2C
    if (p.product === 'managedCloudToCloudBackup' && c2cModel !== 'both' && p.c2cModel !== c2cModel) continue;

    const s = positionsSumme(p, menge, ctx, eingabe.preise?.[key]);
    zeilen.push({ key, label: p.label, menge, ...s });
    const t = (proProdukt[p.product] ??= { vk: 0 });
    t.vk = cent(t.vk + s.vk);
  }

  const vk = cent(Object.values(proProdukt).reduce((a, v) => a + v.vk, 0));
  return { zeilen, proProdukt, gesamt: { vk }, warnungen: pruefe(positions, eingabe) };
}

/** Deutsche Währungsformatierung mit immer zwei Nachkommastellen ("109,90 €"). */
export function euro(x) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(x);
}
